import { holdUnsaved } from "@/hooks/use-unsaved-draft";

/**
 * How long a write may wait for its answer. A write that never answers holds every write
 * queued behind it, so one silent request would stop an editor's saves with nothing on screen.
 */
export const WRITE_TIMEOUT_MS = 30_000;

/** An upload carries the file's bytes before it is answered. */
export const UPLOAD_TIMEOUT_MS = 10 * 60_000;

/** Erasing a draft for good removes each file and version before it answers. */
export const ERASE_TIMEOUT_MS = 5 * 60_000;

export const WRITE_TIMED_OUT = "WRITE_TIMED_OUT";

export const TIMED_OUT_WORDS =
  "The server did not answer, so this change may not be saved. Try again.";

/**
 * Send one write and read its answer under one deadline, counted as unsaved until it settles.
 * The body is under it too: headers followed by a stalled body hold a queue just the same.
 */
export async function underDeadline<T>(
  send: (signal: AbortSignal) => Promise<T>,
  deadlineMs: number,
  timedOut: () => Error,
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expired = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      // Refused before the cancel lands, so the write's own abort error never wins the race.
      reject(timedOut());
      controller.abort();
    }, deadlineMs);
  });
  const release = holdUnsaved();
  try {
    return await Promise.race([send(controller.signal), expired]);
  } finally {
    clearTimeout(timer);
    release();
  }
}
